import {
  Component,
  DestroyRef,
  OnInit,
  ViewContainerRef,
  TemplateRef,
  ChangeDetectorRef,
  HostListener,
  ChangeDetectionStrategy,
  isDevMode,
  OnDestroy,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Router, NavigationEnd, ActivatedRoute, RouterOutlet } from '@angular/router';

// Services
import {
  StorageService,
  SharedChangesService,
  APIFacadeService,
  NextInterface,
  ErrorInterface,
  CompleteInterface,
  API_TYPES,
  ERROR_TYPES,
  FeatureFlagService,
} from '@shared/services';
import { WindowRef } from '@shared/global';
import { Client } from '@shared/models/client/client.class';
import { DialogsService } from '@shared/services/dialogs/dialogs.service';
import { TranslateService } from '@shared/services/translate/translate.service';

import { Translator } from '@shared/services/translate/translate.class';

import { filter, debounceTime, takeUntil } from 'rxjs/operators';
import { PaginationResult } from '@shared/models/pagination/pagination-result.class';
import { Utilities } from '@shared/models/utilities/utilities.class';
import { Title } from '@angular/platform-browser';
import { IntercomService } from '@modules/intercom';
import { CacheMapService } from '@shared/services/cache/cache-map.service';
import { HttpEventType } from '@angular/common/http';
import { forkJoin, ReplaySubject, Subject } from 'rxjs';
import { Platform } from '@angular/cdk/platform';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ClientProfileComponent } from '@modules/profile/components/client-profile/client-profile.component';
import { CommonUtilities } from '@shared/models/common/common-utilities.class';
import { SIDEBAR_NAV_TREE } from '@shared/models/navigation/sidebar-nav.config';
import { Bind } from 'primeng/bind';
import { Drawer } from 'primeng/drawer';
import { ClientProfileComponent as ClientProfileComponent_1 } from '../modules/profile/components/client-profile/client-profile.component';
import { ListComponent } from '../modules/side-bar/components/list/list.component';
import { NgClass } from '@angular/common';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { Tooltip } from 'primeng/tooltip';
import { StoreSwitcherComponent } from '../shared/components/v2/store-switcher/store-switcher.component';
import { ProgressSpinner } from 'primeng/progressspinner';
import { PullDownDirective } from '../shared/directives/pull-down.directive';
import { ProgressBarComponent } from '../shared/components/progress-bar/progress-bar.component';
import { TrialToolbarComponent } from '../shared/components/trial-toolbar/trial-toolbar.component';
import { BreadCrumbNavComponent } from '../modules/bread-crumbs-navigation/components/bread-crumb-nav/bread-crumb-nav.component';
import { StaffProfileComponent } from '../modules/staff-profile/components/staff-profile/staff-profile.component';
import { ShopboxPermissionComponent } from '../shared/components/shopbox-permission/shopbox-permission.component';
import { ShopboxAiPanelComponent } from '../shared/components/v2/shopbox-ai-panel/shopbox-ai-panel.component';

@Component({
  selector: 'sb-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [APIFacadeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Bind,
    Drawer,
    ClientProfileComponent_1,
    ListComponent,
    NgClass,
    CdkCopyToClipboard,
    Tooltip,
    StoreSwitcherComponent,
    ProgressSpinner,
    PullDownDirective,
    ProgressBarComponent,
    TrialToolbarComponent,
    BreadCrumbNavComponent,
    StaffProfileComponent,
    RouterOutlet,
    ShopboxPermissionComponent,
    ShopboxAiPanelComponent,
  ],
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly _destroyRef = inject(DestroyRef);
  readonly clientProfile = viewChild<ClientProfileComponent>('clientProfile');
  readonly navListMenu = viewChild<ListComponent>('shopboxList');
  private readonly _storeSwitcherTpl = viewChild.required<TemplateRef<void>>('storeSwitcherTpl');

  private readonly _featureFlags = inject(FeatureFlagService);
  private _overlayRef: OverlayRef | null = null;
  private refreshSubject: ReplaySubject<any> = new ReplaySubject();
  private easterEgg = 0;
  private translator: Translator;

  private clientPagination: PaginationResult<any>;

  Company = {
    companies: [],
    stores: [],
  };

  // ─── Signals
  selectedCompany = signal<Client | null>(null, { equal: () => false });
  showStores = signal(false);
  supportedLanguages = signal<object[]>([]);
  showProgress = signal(false);
  isLoadingMore = signal(false);
  isSearching = signal(false);

  isSmallScreen = signal(
    typeof window !== 'undefined' && window.matchMedia(Breakpoints.XSmall).matches,
  );

  companySearch = signal('');
  companiesFiltered = signal<any[]>([]);

  translatedRefreshLabel = signal('');
  translatedSearch = signal('');
  translatedStoresList = signal('');
  translatedClientId = signal('');
  translatedClickToCopyClientId = signal('');

  // ─── Non-reactive state
  currentRouter: string;
  imageUrl: any;
  currentComponent: any;
  accountEmail: string = '';
  UserIsVip = false;
  topMove = 0;
  rotateArrow = 0;
  startTouch = false;
  endTouch = true;
  interComConfig: any;
  isNativeApp: boolean = false;
  isFirstTime: boolean = false;
  trialDaysLeft: number = 0;
  currencyTemp: boolean = false;
  isApp: boolean = false;
  hideMainNav: boolean = false;
  isTrialClient: boolean = false;
  isTrialEnded: boolean = false;
  trialStartDate: string;
  trialEndDate: string;
  keyRouteName: string = '__tn';
  isMobile: boolean = new Utilities().isMobileDevice();
  isTouchDevice: boolean = new Utilities().isTouchDevice();
  hover: boolean;

  callBackUrl = undefined;

  /** When true, backoffice content is hidden (Staff / Manager). */
  isAccessBlocked = signal(false);

  migrationCompleted: boolean = false;
  migrationDate: any = null;
  currentCurrency: string = undefined;
  private ngUnsubscribe: Subject<void> = new Subject();

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.key === 'r' && event.altKey) {
      this._cache.deleteAllCaches();
      this.refreshTab();
    }
  }

  constructor(
    private _router: Router,
    private _storage: StorageService,
    private _dialog: DialogsService,
    private _viewRef: ViewContainerRef,
    private _changeDetectRef: ChangeDetectorRef,
    private _windRef: WindowRef,
    private _intercom: IntercomService,
    private _sharedChanges: SharedChangesService,
    private _title: Title,
    private _cache: CacheMapService,
    // private _ga: GaService,
    private _api: APIFacadeService,
    private _activeRouter: ActivatedRoute,
    private _platform: Platform,
    private _overlay: Overlay,
    public translate: TranslateService,
    private _bp: BreakpointObserver,
  ) {
    _storage.setHeadLine(true);
    _storage.setBreadCrumb(false);
    _storage.setStaffProfile(false);
    _storage.setTrialToolbarStatus(false);
    this.translator = new Translator();
    this.subscribeToApi();
    this.watchRouting();
    // @FIXME: @WAITING: https://github.com/angular/components/issues/17220
    // if ((_platform.IOS || _platform.ANDROID) && (!_platform.SAFARI && !_platform.FIREFOX && !_platform.EDGE && !_platform.TRIDENT)) {
    //   this.isApp = true;
    //   _storage.setIsAppTemp('true');
    // } else if((navigator.userAgent.match(/Windows Phone/i) || navigator.userAgent.match(/iemobile/i) || navigator.userAgent.match(/WPDesktop/i)) && (!_platform.SAFARI && !_platform.FIREFOX && !_platform.WEBKIT && !_platform.TRIDENT)){
    //   this.isApp = true;
    //   _storage.setIsAppTemp('true');
    // }
    this.detectIOSPlatform();
    this.detectWindowsPhonePlatform();
    this.detectAndroidPlatform();
    if (!this._router.url.includes('create-account')) {
      this.setProductSetting();
    }
    this.getParams();
    this.authorizeShopify();

    this.migrationCompleted = this._storage.getMigrationCompleted() === 'true' ? true : false;

    requestAnimationFrame(() => {
      this.getMigrationData();
    });
  }

  getParams() {
    const paramsSub: any = this._activeRouter.snapshot.queryParams;
    const utility = new Utilities();
    const callBackUrl = utility.getParameterCaseInsensitive(paramsSub, 'callback_url');

    if (callBackUrl) {
      const decodeUrl = decodeURIComponent(callBackUrl);
      if (decodeUrl.charAt(0) == '/') {
        this.callBackUrl = callBackUrl.slice(1);
      } else {
        this.callBackUrl = decodeUrl;
      }
    }
  }

  getMigrationData() {
    this._api.shopboxApiService.clientService.getDataMigrationSettings().subscribe(
      (res: any) => {
        if (res && Array.isArray(res) && res.length === 0) {
          this._storage.setMigrationDate(null);
          this.migrationDate = null;
        } else if (res) {
          this._storage.setMigrationDate(res);
          this.migrationDate = this._storage.getMigrationDate();
        }
      },
      (error: any) => {
        this._dialog.toast(error, this._viewRef, 3200, true);
      },
    );
  }

  detectIOSPlatform() {
    const navigator: any = window.navigator;
    const standalone = navigator.standalone;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const safari = /safari/.test(userAgent);
    const ios = /iphone|ipod|ipad/.test(userAgent);
    const fireFox = this._platform.FIREFOX;
    // const chrome  = this._platform.WEBKIT;
    const edge = this._platform.EDGE;
    const trident = this._platform.TRIDENT;

    if (!standalone && !safari && !fireFox && !edge && !trident && ios) {
      this.isApp = true;
      this._storage.setIsAppTemp('true');
    }
  }

  detectAndroidPlatform() {
    const navigator: any = window.navigator;
    const standalone = navigator.standalone;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const safari = /safari/.test(userAgent);
    const android = this._platform.ANDROID;
    const fireFox = this._platform.FIREFOX;
    // const chrome  = this._platform.WEBKIT;
    const edge = this._platform.EDGE;
    const trident = this._platform.TRIDENT;

    if (!standalone && !safari && !fireFox && !edge && !trident && android) {
      this.isApp = true;
      this._storage.setIsAppTemp('true');
    }
  }

  detectWindowsPhonePlatform() {
    const navigator: any = window.navigator;
    const standalone = navigator.standalone;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const safari = /safari/.test(userAgent);
    const windowsPhone = /Windows Phone|iemobile|WPDesktop/.test(userAgent);
    const fireFox = this._platform.FIREFOX;
    // const chrome  = this._platform.WEBKIT;
    const edge = this._platform.EDGE;
    const trident = this._platform.TRIDENT;

    if (!standalone && !safari && !fireFox && !edge && !trident && windowsPhone) {
      this.isApp = true;
      this._storage.setIsAppTemp('true');
    }
  }

  watchRouting() {
    this.hideMainNav = this._isSystemDesignUrl();
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(() => {
        this.checkCreationForIntercom();
        this.watchTitle();
        this.hideMainNav = this._isSystemDesignUrl();
        if (this.isSmallScreen()) {
          this.navVisible = false;
        }
        this._changeDetectRef.markForCheck();
      });
  }

  /** Routes that supply their own sidebar and should suppress the main app drawer. */
  private _isSystemDesignUrl(): boolean {
    const firstSegment = this._router.url.split(/[?#]/)[0].split('/').filter(Boolean)[0];
    return firstSegment === 'sd';
  }

  checkCreationForIntercom() {
    this._intercom.update({
      hide_default_launcher: false,
    });
  }

  subscribeToApi() {
    this._api.subscribeToApi(this._changeDetectRef, 400);
    this._api.nextEmit.subscribe((res: NextInterface) => {
      this.nextFnApi(res.res, res.apiType);
    });
    this._api.errorEmit.subscribe((res: ErrorInterface) => {
      this.errorFnApi(res.error, res.errorType);
    });
    this._api.completeEmit.subscribe((res: CompleteInterface) => {
      this.completeFnApi(res.apiType);
    });
  }

  nextFnApi(res: any, type: API_TYPES) {
    switch (type) {
      case API_TYPES.GET_SINGLE_CLIENT_MAIN:
        this.setSingleClient(res);
        break;
      case API_TYPES.GET_COUNTRIES:
        this.setCurrency(res);
        break;
      case API_TYPES.GET_ALL_CLIENTS:
        this.setAllClients(res);
        break;
      case API_TYPES.UPDATE_CLIENT_IMAGE:
        this.setUpdateImage(res);
        break;
      // case API_TYPES.GET_CLIENT_ROLES:
      //   this.setClientRoles(res);
      //   break;

      default:
        break;
    }
  }

  errorFnApi(error: any, type: ERROR_TYPES) {
    if (type === ERROR_TYPES.NORMAL) {
      this._dialog.toast(error, this._viewRef, 3200, true);
    }
    if (this._api.apiType === API_TYPES.UPDATE_CLIENT_MAIN) {
      this._dialog.toast('Can not Change the Logo of current client', this._viewRef, 3200, true);
    }
    this.changeProgress(false);
  }

  completeFnApi(type: API_TYPES) {
    switch (type) {
      case API_TYPES.GET_COUNTRIES:
        this.setCurrencyComplete();
        this.changeProgress(false);
        break;
      case API_TYPES.GET_ALL_CLIENTS:
        this.setAllClientsComplete();
        this.isLoadingMore.set(false);
        this.isSearching.set(false);
        this.changeProgress(false);
        break;
      case API_TYPES.UPDATE_CLIENT_IMAGE:
        this._dialog.toast('Logo Changed', this._viewRef, 3200, false);
        this.changeProgress(false);
        break;
      case API_TYPES.UPDATE_CLIENT_MAIN:
        this.editClientComplete();
        this.changeProgress(false);
        break;
      // case API_TYPES.GET_CLIENT_ROLES:
      //   this.changeProgress(false);
      //   break;

      default:
        this.changeProgress(false);
        break;
    }
  }

  authorizeShopify() {
    if (this.callBackUrl) {
      const clientId = this._storage.getClientUID();
      const accessToken = CommonUtilities.getToken();
      let urlNew;
      if (this.callBackUrl.includes('?')) {
        urlNew =
          this._api.shopboxApiService.accountService.getURL('', '')['url'] +
          this.callBackUrl +
          `&accessToken=${accessToken}&client=${clientId}`;
      }
      if (!this.callBackUrl.includes('?')) {
        urlNew =
          this._api.shopboxApiService.accountService.getURL('', '')['url'] +
          this.callBackUrl +
          `?accessToken=${accessToken}&client=${clientId}`;
      }
      this.openInSameTab(urlNew);
    }
  }

  openInSameTab(url: string) {
    this._windRef.nativeWindow.open(url, '_parent');
  }

  setSettingsForClient() {
    // @TODO: Make the navigation service control all the navigations
    const subData = this._storage.getSubscriptionPlan();
    const notActiveSubData = this._storage.getSubscriptionNotActivePlan();
    const notActiveNewSubData = this._storage.getNewSubscriptionPlan();
    const subscriptionEnable = this._storage.getIsSubscriptionEnabled();
    const paramsSub: any = this._activeRouter.snapshot.queryParams;
    const confirmParam = new Utilities().getParameterCaseInsensitive(paramsSub, 'confirmed');
    const clientParam = new Utilities().getParameterCaseInsensitive(paramsSub, 'client');
    const company = this.selectedCompany();
    const isPaid = company.paymentInfo;
    const isPartnerAndPaymentByCardAndPaymentNotExist =
      company.isPartner && company.isPartnerCardType && !isPaid;
    const isFutureSubscription = company.subscribedInFuture;
    this.isTrialClient = this._storage.getCurrentClient()['isTrial'];
    this.isTrialEnded = this._storage.getCurrentClient()['isTrialEnded'];

    if (this._storage.getIsTester() === 'true') {
      this.setTesterSettings();
    }

    // First: check on chargbee subscription if enabled
    else if (
      Array.isArray(notActiveNewSubData) &&
      notActiveNewSubData.length > 0 &&
      subscriptionEnable
    ) {
      if (this._router.url.includes('create-account')) {
        this._router.navigate(['dashboard']);
      }
    } else {
      if (
        !subData &&
        !notActiveSubData &&
        subscriptionEnable &&
        (!Array.isArray(notActiveNewSubData) || !notActiveNewSubData.length)
      ) {
        if (!this.isTrialClient) {
          this._storage.setFirstTimeCreation(true);
          if (!this._router.url.includes('create-account')) {
            this._router.navigateByUrl('/create-account/choose-subscription');
          }
        } else {
          if (this._router.url.includes('create-account')) {
            this._router.navigate(['dashboard']);
          }
          this.checkTrialClientRoute();
        }
      } else {
        if (
          this._router.url.includes('create-account') &&
          !isPartnerAndPaymentByCardAndPaymentNotExist
        ) {
          if (this.isTrialClient || subData || !subscriptionEnable) {
            if (!(confirmParam === 'true' && clientParam)) {
              this._router.navigate(['dashboard']);
            }
          }
        } else if (
          !this.isTrialClient &&
          notActiveSubData &&
          subscriptionEnable &&
          !isPartnerAndPaymentByCardAndPaymentNotExist
        ) {
          this._router.navigateByUrl('/create-account/payment');
        } else if (
          isPartnerAndPaymentByCardAndPaymentNotExist ||
          (isFutureSubscription && company.isPartnerCardType && !isPaid)
        ) {
          this._router.navigateByUrl('account-settings/plan-settings/payment-cards');
        } else if (company.isPartnerCardType && company.isHolded) {
          this._router.navigateByUrl('account-settings/plan-settings/payment-cards');
        }
        if (subscriptionEnable) {
          this.checkTrialClientRoute();
        }
      }
    }
    this.setTrialSettings();
    this._changeDetectRef.markForCheck();
  }

  setTesterSettings() {
    if (
      isDevMode() &&
      this._router.url.includes('create-account/company-settings') &&
      this._storage.getIsTester() === 'true'
    ) {
      if (this.clientProfile()) {
        this.closeStoresListFn();
      }
      this.selectedCompany.set(null);
      this._storage.deleteCurrentClient();
      this._storage.deleteCurrentCurrency();
      this._storage.deleteClientsSettings();
      this._storage.deleteClient();
      this._dialog.toast(`Don't test it as a NASA application :D`, this._viewRef, 5000, undefined);
    } else {
      this._storage.deleteIsTester();
      this.checkClient();
    }
  }

  setTrialSettings() {
    const company = this.selectedCompany();
    if (company) {
      this.isTrialClient = company.isTrial;
      this.isTrialEnded = company.isTrialEnded;
      this.trialStartDate = company.trialStartDate;
      this.trialEndDate = company.trialEndDate;
      this.trialDaysLeft = this.getDaysLeft();
    }
    this.setTrialToolbar(this.isTrialClient);
    this._changeDetectRef.markForCheck();
  }

  /**
   * getCurrentClient
   * @function
   * @description get current client from server with the specific id
   * @memberof AppComponent
   */
  getCurrentClient() {
    const clientId = this._storage.getClientUID();
    const getSingleClientSub = this._api.shopboxApiService.clientService.getSingleClient(clientId);
    this._api.apiType = API_TYPES.GET_SINGLE_CLIENT_MAIN;
    this._api.errorType = ERROR_TYPES.NORMAL;
    this._api.apiSubject.next(getSingleClientSub);
  }

  setSingleClient(res: any) {
    const client: any = new Client(res);
    client['role'] = res.role;
    client['hideRefund'] = res.hide_refund;
    this.selectedCompany.set(client);
    this.updateCurrentStoreCache();
  }

  setInterComBoot() {
    this.interComConfig = {
      app_id: isDevMode() ? 'v0y4bjwm' : 'qwrwacko',
      updateOnRouterChange: true,
      hide_default_launcher: false,
    };

    // Include user data in boot call to send user details with first request
    if (this._storage.getAccount()) {
      const userData = this._storage.getIntercomUserData();
      this.interComConfig = {
        ...this.interComConfig,
        ...userData,
      };
    }

    this._intercom.boot(this.interComConfig);
  }

  setTitle(newTitle: string) {
    this._title.setTitle(newTitle);
  }

  getRouteTitle(): string {
    let title = '';
    const URL = this._router.url;
    const findURL = URL.split('/')[1];
    const findURLTitle = findURL.split('?')[0];
    if (
      findURLTitle === 'login' ||
      findURLTitle === 'reset' ||
      findURLTitle === 'create-company' ||
      findURLTitle === 'signup'
    ) {
      title = 'myShopbox - ' + findURLTitle;
    } else {
      const name = this.getHeaderTitle();
      title = name ? 'myShopbox - ' + name : 'myShopbox';
    }
    return title;
  }

  // This is no longer used after navigation redesign
  // getRouter() {
  //   return this._router.url;
  // }

  ngOnInit() {
    this.isApp = this._storage.getIsApp() === 'true';
    this.ensurePosCurrency();

    this.initBreakpointObserver();
    this.refreshTrans();
    this.clientWatch();
    this.currencyWatch();
    this.refreshObserv();
    this.updateTranslations();
    this.checkAccessRestriction();

    this._sharedChanges.sidebarToggle
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.toggleSideNav());

    this._sharedChanges.routeReload
      .pipe(debounceTime(2000), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.redirectTo(this._router.url));

    this.getAllowedFeatures();

    // Blocked users have no router-outlet, so onActivate never runs — fetch identity roles once here (do not call getClientRoles from checkAccessRestriction: setClientRoles invokes it and would recurse).
    if (this.isAccessBlocked()) {
      this.getClientRoles();
    }
  }

  checkAccessRestriction(): void {
    this.isAccessBlocked.set(this._storage.isRoleRestricted());
    // Without a router-outlet child, onActivate never runs — mirror its shell setup for blocked users.
    if (this.isAccessBlocked()) {
      this.setTesterSettings();
      if (this._storage.getAccount()) {
        this.setInterComBoot();
        this.accountEmail = this._storage.getAccount()['username'];
      }
      this.setDashboardSettings();
      this.setDrawerAndHeaderIfNot(true);
      this.updateCurrentClient();
      this._changeDetectRef.markForCheck();
    }
  }

  // XSmall breakpoint <= 599px as per CDK Breakpoints
  private initBreakpointObserver(): void {
    const bpSub = this._bp.observe(Breakpoints.XSmall).subscribe((state) => {
      const wasSmall = this.isSmallScreen();
      this.isSmallScreen.set(state.matches);

      if (wasSmall && !this.isSmallScreen()) {
        this.navVisible = true;
        if (this._storage.getDrawerStatus() === undefined) {
          this.setDrawerStatus(true);
        }
      } else if (!wasSmall && this.isSmallScreen()) {
        // Transitioned desktop → mobile: hide sidebar
        this.navVisible = false;
      } else if (!state.matches && !this._storage.getDrawerVisibility()) {
        // First observation on desktop: sidebar is not visible (undefined on fresh install,
        // or false written by logout) — initialize to open as default desktop state
        this.navVisible = true;
        if (!this._storage.getDrawerStatus()) {
          this.setDrawerStatus(true);
        }
      }
    });
    this._api.subscriptionList.add(bpSub);
  }

  updateCurrentClient() {
    const currentClient: any = this._activeRouter.children[0]
      ? this._activeRouter.children[0].snapshot.data.currentClient
      : undefined;
    if (currentClient && currentClient.uid) {
      const client: Client = new Client(currentClient);
      const company = this.selectedCompany();
      client['role'] = company ? company.role : currentClient.role;
      client['hideRefund'] = currentClient['hide_refund'];
      this.selectedCompany.set(client);
      this._api.shopboxApiService.clientService.updateCurrentClient(client);
      this.checkTrialClientRoute();
    }
  }

  refreshTrans() {
    const translateChanges = this.translate.onLangChanged.subscribe(() => {
      this.watchTitle();
      this.updateTranslations();
    });
    this._api.subscriptionList.add(translateChanges);
  }

  watchTitle() {
    const name = this.getRouteTitle();
    const translatedName = this.translate.instantUse(name, 'en');
    this.setTitle(translatedName);
    this._changeDetectRef.markForCheck();
  }

  clientWatch() {
    const clientChanges = this._sharedChanges.currentClientChange.subscribe((res) => {
      if (res) {
        this.selectedCompany.set(res);
        this._storage.updateIntercomClient(this._intercom);
      }
    });
    this._api.subscriptionList.add(clientChanges);
  }

  reInitComponent() {
    if (this.currentComponent && this.currentComponent.onActivate) {
      this.currentComponent.onActivate(this.currentComponent.currentComponent);
      setTimeout(() => {
        if (this.currentComponent.currentComponent) {
          this.currentComponent.currentComponent.ngOnInit();
        } else {
          this.currentComponent.ngOnInit();
        }
      }, 1000);
    }
  }

  sendDeviceToken() {
    const isNativeApp = this._storage.getIsNativeApp();
    this.isApp = this._storage.getIsApp() == 'true' ? true : false;
    this.isNativeApp = isNativeApp === 'true';
    if (this.isNativeApp) {
      this._api.shopboxApiService.accountService.sendDeviceToken();
    }
  }

  currencyWatch() {
    const changeCurrency = this._sharedChanges.currentCurrencyChange.subscribe((res) => {
      this.currentCurrency = res;
      this._changeDetectRef.markForCheck();
    });
    this._api.subscriptionList.add(changeCurrency);
  }

  setDashboardSettings() {
    this.sendDeviceToken();
    this.getCurrentLang();
    // this.detect();
  }

  getCurrency(temp: boolean) {
    this.currencyTemp = temp;
    const getCountriesSub = this._api.shopboxApiService.countryService.getCountries();
    this._api.apiType = API_TYPES.GET_COUNTRIES;
    this._api.errorType = ERROR_TYPES.NORMAL;
    this._api.apiSubject.next(getCountriesSub);
  }

  setCurrency(res: any) {
    const client = this._api.shopboxApiService.countryService.getCurrentClient();
    this.setCurrencyForClient(client, res, this.currencyTemp);
  }

  private ensurePosCurrency(): void {
    if (!this.isApp) return;

    const clientId = Number(this._storage.getClientUID());
    if (!clientId) return;

    const resolveCurrencySub = forkJoin({
      client: this._api.shopboxApiService.clientService.getSingleClient(clientId),
      countries: this._api.shopboxApiService.countryService.getCountries(),
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ({ client, countries }) => {
          const currentClient: any = new Client(client);
          currentClient['role'] = client.role || this._storage.getPermission();
          currentClient['hideRefund'] = client.hide_refund;
          this.selectedCompany.set(currentClient);
          this._storage.setCurrentClientTemp(currentClient);
          this.setCurrencyForClient(currentClient, countries, true);
        },
        error: () => undefined,
      });

    this._api.subscriptionList.add(resolveCurrencySub);
  }

  private setCurrencyForClient(client: Client, countries: any, temp: boolean): boolean {
    const countryId = client?.countryId;
    if (!countryId || !countries?.data?.length) return false;

    const country = countries.data.find(
      (element: any) => Number(element.uid) === Number(countryId),
    );
    const currency = country?.cn_currency_iso_3;
    if (!currency) return false;

    this.currentCurrency = currency;
    if (temp) {
      this._storage.setCurrencyTemp(currency);
    } else {
      this._storage.setCurrency(currency);
    }
    this._sharedChanges.sharedCurrencyChanges(currency);
    this._changeDetectRef.markForCheck();
    return true;
  }

  setCurrencyComplete() {
    // if (!this.currentClientNotExist) {
    //   this.checkClient();
    // }
    // this.companySearch = '';
    this._api.shopboxApiService.clientService.updateCurrentClient(this.selectedCompany());
    this.reInitComponent();
  }

  setDrawerVisibility(val: boolean) {
    if (this._storage.getTokenTemp()) {
      this._storage.setDrawerVisibilityTemp(val);
    } else {
      this._storage.setDrawerVisibility(val);
    }
  }

  setDrawerStatus(val: boolean) {
    if (this._storage.getTokenTemp()) {
      this._storage.setDrawerStatusTemp(val);
    } else {
      this._storage.setDrawerStatus(val);
    }
  }

  setHeaderToolbarVisibility(val: boolean) {
    if (this._storage.getTokenTemp()) {
      this._storage.setHeaderToolbarVisibilityTemp(val);
    } else {
      this._storage.setHeaderToolbarVisibility(val);
    }
  }

  getDrawerVisibility(): boolean {
    return this._storage.getDrawerVisibility();
  }

  getDrawerStatus(): boolean {
    return this._storage.getDrawerStatus();
  }

  /** Sidebar width:
   *  - Mobile: always 100vw (sidebar is either hidden or full-width)
   *  - Desktop expanded: 248px
   *  - Desktop collapsed: 72px */
  get drawerWidth(): string {
    if (this.isSmallScreen()) return '100vw';
    return this.getDrawerStatus() ? '248px' : '72px';
  }

  /** Main content left offset — only on desktop where the sidebar is persistent. Mobile = 0. */
  get contentMarginLeft(): string {
    if (this.isApp || this.hideMainNav || this.isSmallScreen() || !this.navVisible) return '0';
    return this.getDrawerStatus() ? '248px' : '72px';
  }

  getHeaderToolbarVisibility(): boolean {
    return this._storage.getHeaderToolbarVisibility();
  }

  getRefreshBarVisibility(): boolean {
    return !this.getHeaderToolbarVisibility();
  }

  onActivate(ev: any) {
    this.setTesterSettings();
    this.currentComponent = ev;
    if (this._storage.getAccount()) {
      this.setInterComBoot();
      this.accountEmail = this._storage.getAccount()['username'];
    }
    this.setDashboardSettings();
    this.setDrawerAndHeaderIfNot(true);
    this.updateCurrentClient();
    this.getClientRoles();
    this._changeDetectRef.markForCheck();
  }

  // @FIXME: Fix this shit and reduce the duplications
  // // life motto
  // if (sad() === true) {
  //  sad.stop.();
  //  beAwesome();
  //  continue;
  // }
  setDrawerAndHeaderIfNot(val: boolean) {
    this.isApp = this._storage.getIsApp() === 'true' ? true : false;
    if (!this.isApp) {
      if (this.clientProfile() && this.clientProfile()!.getDrawerStatus() === val) {
        this.setHeaderToolbarVisibility(val);
        this.setDrawerVisibility(val);
        this.setDrawerStatus(val);
      } else {
        if (this._storage.getDrawerStatus() === undefined) {
          this.setDrawerStatus(val);
        }
        if (
          this._storage.getDrawerVisibility() === undefined ||
          this._storage.getHeaderToolbarVisibility() === false
        ) {
          this.setDrawerVisibility(val);
        }
        if (
          this._storage.getHeaderToolbarVisibility() === undefined ||
          this._storage.getHeaderToolbarVisibility() === false
        ) {
          this.setHeaderToolbarVisibility(val);
        }
      }
    }
  }

  // Unused: only setDrawerAndHeaderIfNot is called from onActivate
  // setDrawerAndHeader(val: boolean) {
  //   this.isApp = this._storage.getIsApp() === 'true' ? true : false;
  //   if (!this.isApp) {
  //     this.setDrawerVisibility(val);
  //     this.setDrawerStatus(val);
  //     this.setHeaderToolbarVisibility(val);
  //   }
  // }

  // Unused: searchInCompanies UID branch is commented out below
  // regexUID(str: string): boolean {
  //   const regexUID = new RegExp('^UID:');
  //   return regexUID.test(str);
  // }

  searchInCompanies(event: string) {
    // if (this.regexUID(event)) {
    //   const searchBy = event.substring(event.indexOf(':') + 1);
    //   this.companiesFiltered.set(new Utilities().filterIt(this.Company.companies, 'uid', searchBy));
    // } else {
    //   event = new Utilities().trimRightAndLeft(event);
    //   this.companiesFiltered.set(new Utilities().filterIt(this.Company.companies, 'name', event));
    // }

    event = new Utilities().trimRightAndLeft(event);
    this.companySearch.set(event);
    this.isSearching.set(true);
    this.Company.companies = [];
    this._cache.deleteAllCachesContain(Client.URL);
    this.getAllClients(1, 50, event);
  }

  resetSearchInCompanies(_event) {
    this.companySearch.set('');
    this.Company.companies = [];
    this.companiesFiltered.set([]);
    this.getAllClients();
    // this.companiesFiltered.set(new Utilities().filterIt(this.Company.companies, 'name', ''));
  }

  // Unused (not referenced from template or other modules)
  // getRefreshBtnStatus(): boolean {
  //   return true;
  // }

  // refreshTabBtn() {
  //   this._cache.deleteAllCaches();
  //   this._sharedChanges.sharedCountChanges(true);
  //   this.refreshTab();
  // }

  refreshTab() {
    this.refreshSubject.next(true);
    this._changeDetectRef.markForCheck();
  }

  refreshObserv() {
    const refreshChanges = this.refreshSubject.pipe(debounceTime(2000)).subscribe(() => {
      this.redirectTo(this._router.url);
    });
    this._api.subscriptionList.add(refreshChanges);
  }

  redirectTo(uri: string) {
    // @TODO: LOAD CURRENT CLIENT
    this.reInitComponent();
  }

  onDeactivate(_ev) {
    this._changeDetectRef.markForCheck();
  }

  getDaysLeft(): number {
    this.trialDaysLeft = 0;
    const date1: Date = new Date();
    const date2: Date = new Date(this.trialEndDate);
    const timeDiff = date2.getTime() - date1.getTime();
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return diffDays;
  }

  checkTrialClientRoute() {
    this.isTrialClient = this._api.shopboxApiService.clientService.getIsTrialClient();
    this.trialDaysLeft = this.getDaysLeft();
    if (this.isTrialClient && this.isTrialEnded) {
      this._router.navigateByUrl('account-settings/plan-settings/choose-plan');
    }
    this.changeProgress(false);
  }

  private getCurrentLang() {
    this.translate.setCurrentLang();
    const current = this.translate.currentLang;
    if (current) {
      this.supportedLanguages.set(this.translator.supportedLanguages);
      this.supportedLanguages.set(this.translator.disabledLanguages(current));
    } else {
      this.translate.setDefaultLang();
      this.supportedLanguages.set(this.translator.supportedLanguages);
      this.supportedLanguages.set(this.translator.disabledLanguages(this.translate.currentLang));
    }
  }

  toggleMenu(code: string) {
    this.supportedLanguages.set(this.translator.disabledLanguages(code));
  }

  /**
   * getAllStores
   * @function
   * @description Get All Clients
   * @param {Boolean} notExist if currentClient not found
   * @param {number} pageN number of page to get
   */
  private getAllClients(pageN?: number, pageSize?: number, searchKeyword?: any) {
    const getAllClientsSub = this._api.shopboxApiService.clientService.getClients(
      pageN,
      null,
      pageSize,
      searchKeyword,
    );
    this._api.apiType = API_TYPES.GET_ALL_CLIENTS;
    this._api.errorType = ERROR_TYPES.NORMAL;
    this._api.apiSubject.next(getAllClientsSub);
  }

  setAllClients(res: any) {
    if (res && res.meta) {
      this.clientPagination = new PaginationResult(
        res.meta ? res.meta.pagination : null,
        res.meta ? (res.meta.pagination ? res.meta.pagination.links : null) : null,
      );
    }
    if (res && res.data) {
      res.data.forEach((element: any) => {
        if (element.client0) {
          const client: any = new Client(element.client0);
          client['role'] = element.role;
          client['hideRefund'] = element.hide_refund;
          client['branch'] = element.branch;
          this.Company.companies.push(client);
        }
      });
    }
  }

  setAllClientsComplete() {
    if (!this._storage.getClientUID()) {
      this.selectedCompany.set(this.Company.companies[0]);
      if (this.selectedCompany()) {
        this.saveCompany(this.selectedCompany());
      }
    } else if (this._storage.getClientUID() && this.Company.companies.length > 0) {
      // this.selectedCompany.set(this.Company.companies.find(element => String(element.uid) == String(this._storage.getClientUID())) || null);
      if (this.selectedCompany()) {
        this.saveCompany(this.selectedCompany());
      }
    }
    this.companiesFiltered.set(new Utilities().filterIt(this.Company.companies, 'name', ''));
  }

  setProductSetting() {
    const model = {
      sku_gen_enabled: 0,
      barcode_gen_enabled: 0,
      next_sku: null,
    };

    this._api.shopboxApiService.productSettingService.getProductSettings().subscribe(
      (res) => {
        if (res && res.value) {
          this._storage.setProductSetting(res.value);
        } else {
          this._storage.setProductSetting(model);
        }
      },
      (error) => {
        this._storage.setProductSetting(model);
        this._dialog.toast(error, this._viewRef, 3200, true);
      },
    );
  }

  private checkClient() {
    this.selectedCompany.set(this._api.shopboxApiService.clientService.getCurrentClient());
    if (this.selectedCompany()) {
      this.selectedCompany.set(this._api.shopboxApiService.clientService.getCurrentClient());
      this.setSettingsForClient();
    } else {
      this.Company.companies = [];
      this.getAllClients();
    }
  }

  /**
   * saveCompany
   * @function
   * @description [save client in storage]
   * @param {number} client [client Object]
   */
  private saveCompany(client: any): void {
    if (client) {
      if (this._storage.getTokenTemp()) {
        this._storage.setClientTemp(String(client['uid']));
        this._storage.setCurrentClientTemp(client);
        this._storage.setPermissionTemp(client.role);
        this.getCurrency(true);
      } else {
        this._storage.setClient(String(client['uid']));
        this._storage.setCurrentClient(client);
        this._storage.setPermission(client.role);
        this.getCurrency(false);
      }
      this._storage.setTakeAwayActivation(!!client.enableTakeAwayPrice);
    }
  }

  /**
   * selectStore
   * @function
   * @description []
   * @param {number} index [index of store to select]
   * @param {number} id [client id]
   * @return
   */
  selectStore(client: any, id: number) {
    if (client && this.selectedCompany()?.uid === client.uid) {
      return;
    }
    this.selectedCompany.set(client);
    this.saveCompany(this.selectedCompany());
    this._sharedChanges.sharedClientChanges(this.selectedCompany());
    this.setSettingsForClient();
    this.closeStoresListFn();
    if (this.isSmallScreen()) {
      this.navVisible = false;
    }
    this.setProductSetting();
    this.getMigrationData();
    this._storage.deleteWoToken();
    this.getClientRoles();
    this.getAllowedFeatures();
    // this._navigation.canContinue(this._activeRouter.queryParams);
  }

  getAllowedFeatures() {
    this._featureFlags.load().pipe(takeUntil(this.ngUnsubscribe)).subscribe();
  }

  /**
   * getClientRoles
   * @function
   * @description Get client roles from server
   * @memberof HomeComponent
   */
  getClientRoles() {
    this._api.shopboxApiService.accountService
      .getClientRoles()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any) => {
          this.setClientRoles(res);
          this.setClientPermissions(res);
        },
        error: (error) => {
          this._dialog.toast(error, this._viewRef, 3200, true);
        },
      });
  }

  setClientRoles(res: any) {
    if (res && res.client_roles && Array.isArray(res.client_roles) && res.client_roles.length > 0) {
      const clientRoles = res.client_roles[0];
      this._storage.setClientRoles(clientRoles);
    }
    this.checkAccessRestriction();
  }

  setClientPermissions(res: any) {
    if (res && res.permissions && Array.isArray(res.permissions) && res.permissions.length > 0) {
      const clientPermissions = res.permissions;
      this._storage.setClientPermissions(clientPermissions);
    }
    this.checkAccessRestriction();
  }

  updateCurrentStoreCache() {
    this._api.shopboxApiService.clientService.updateCurrentClient(this.selectedCompany());
  }

  emitBarChanged() {
    if (this.isSmallScreen() && !this.getDrawerStatus()) {
      this.navVisible = false;
    }
    if (this.currentComponent && this.currentComponent.sideBarChanged) {
      this.currentComponent.sideBarChanged();
    }
    this._changeDetectRef.markForCheck();
  }

  setShowStores(payload: { open: boolean; event?: Event }) {
    if (payload.open) {
      this.showStores.set(true);
      if (this.companiesFiltered().length === 0) {
        this.Company.companies = [];
        this.companiesFiltered.set([]);
        this._cache.deleteAllCachesContain(Client.URL);
        this.isSearching.set(true);
        this.getAllClients();
      }
      if (payload.event) {
        const trigger = (payload.event.currentTarget ?? payload.event.target) as HTMLElement;
        this._openStoreSwitcher(trigger);
      }
    } else {
      this._closeStoreSwitcher(false);
    }
  }

  private _openStoreSwitcher(trigger: HTMLElement): void {
    if (this._overlayRef?.hasAttached()) return;

    if (this.isSmallScreen()) {
      // Mobile: center horizontally in the viewport, anchor just below the trigger
      const rect = trigger.getBoundingClientRect();
      this._overlayRef = this._overlay.create({
        positionStrategy: this._overlay
          .position()
          .global()
          .centerHorizontally()
          .top(`${rect.bottom + 8}px`),
        scrollStrategy: this._overlay.scrollStrategies.reposition(),
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        width: 'calc(100vw - 40px)',
      });
    } else {
      // Desktop: anchor to the trigger, right side first then fall back to bottom
      const desktopPositions: ConnectedPosition[] = [
        // Primary: to the right of trigger, top-aligned
        { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 10 },
        // Fallback: below trigger (no room on right)
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
      ];
      this._overlayRef = this._overlay.create({
        positionStrategy: this._overlay
          .position()
          .flexibleConnectedTo(trigger)
          .withPositions(desktopPositions)
          .withViewportMargin(8)
          .withPush(false),
        scrollStrategy: this._overlay.scrollStrategies.reposition(),
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        width: '232px',
      });
    }

    this._overlayRef.attach(new TemplatePortal(this._storeSwitcherTpl(), this._viewRef));

    // Backdrop click — user dismissed; sync button icon back
    this._overlayRef.backdropClick().subscribe(() => {
      this._closeStoreSwitcher(true);
    });

    // Could be deleted later, since we are using signals
    // this._changeDetectRef.markForCheck();
  }

  /**
   * @param syncButtonIcon pass true when the popover was dismissed by the user
   *   (backdrop click) so the sidebar toggle icon is flipped back.
   *   Pass false when the code initiates the close (store selected, button re-click)
   *   because the caller already handles the icon toggle.
   */
  private _closeStoreSwitcher(syncButtonIcon: boolean): void {
    this._overlayRef?.dispose();
    this._overlayRef = null;
    if (this.showStores()) {
      this.showStores.set(false);
      if (syncButtonIcon) {
        this.clientProfile()?.iconStoreToggle();
      }
    }

    // Reset search state so every open starts fresh
    this.companySearch.set('');
    this.Company.companies = [];
    this.companiesFiltered.set([]);
  }

  refreshStores() {
    this.companySearch.set('');
    this.isSearching.set(true);
    this.Company.companies = [];
    this.companiesFiltered.set([]);
    this._cache.deleteAllCachesContain(Client.URL);
    this.getAllClients();
  }

  closeStoresListFn() {
    if (this.showStores()) {
      this.clientProfile()?.iconStoreToggle();
    }
    this._closeStoreSwitcher(false);
  }

  // Can't find any use for this function
  openAbout() {
    if (this.easterEgg < 3) {
      this.easterEgg++;
    } else {
      this._dialog.toast(
        'Stop play with button(about) dud -_- it do nothing (-_-)',
        this._viewRef,
        3200,
      );
      this.easterEgg = 0;
    }
  }

  getHeaderTitle(): string {
    const crumbs = this.resolveRouteBreadcrumbs();
    const parts = [crumbs.parent, crumbs.child].filter(Boolean);
    return parts.length ? parts.join(' › ') : '';
  }

  // no longer used after navigation redesign
  // getHeaderBreadcrumbs(): { parent: string; child: string | null } {
  //   return this.resolveRouteBreadcrumbs();
  // }

  setRouterName(value: string) {
    if (value) {
      sessionStorage.setItem(this.keyRouteName, value);
    } else {
      sessionStorage.removeItem(this.keyRouteName);
    }
  }

  getRouterName(): string {
    return sessionStorage.getItem(this.keyRouteName);
  }

  private resolveRouteBreadcrumbs(): { parent: string; child: string | null } {
    const cleanUrl = this._router.url.split('?')[0].split('#')[0].replace(/^\/+/, '');
    const segments = cleanUrl.split('/').filter(Boolean);
    const parentSegment = segments[0];

    const parentEntry = parentSegment
      ? SIDEBAR_NAV_TREE.find((e) => e.route.toLowerCase() === parentSegment.toLowerCase())
      : null;

    if (!parentEntry) {
      const fallback = this.getRouterName();
      return { parent: fallback ? this.translate.instant(fallback) : '', child: null };
    }

    const parentLabel = this.translate.instant(parentEntry.name);

    if (parentEntry.children?.length) {
      const childEntry = [...parentEntry.children]
        .sort((a, b) => b.route.length - a.route.length)
        .find((c) => cleanUrl.toLowerCase().startsWith(c.route.toLowerCase()));
      if (childEntry) {
        return { parent: parentLabel, child: this.translate.instant(childEntry.name) };
      }
    }

    return { parent: parentLabel, child: null };
  }

  /**
   * onScroll
   * @function
   * @description [get data on scroll to bottom of list]
   * @return Observable
   */
  onScroll(event: any): void {
    if (event === 'bottom') {
      if (this.clientPagination && this.clientPagination.current) {
        if (this.clientPagination.increaseCurrent()) {
          this.isLoadingMore.set(true);
          return this.getAllClients(this.clientPagination.current);
        }
      }
    }
  }

  /**
   * isEmptyContact
   * @function
   * @description [Check if empty Id]
   * @param uid [uid of client]
   * @returns {Boolean}
   */
  isEmptyContact(uid: number): boolean {
    let empty = true;
    if (uid) {
      empty = false;
    }
    return empty;
  }

  uploadOpen(event: any) {
    const file: any = event.target.files[0];
    const isImage = this.isImageFile(file.name);
    if (!isImage) {
      this._dialog.alert('Invalid file type', this._viewRef);
      return;
    }
    this.loadFile(file);
  }

  getExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts[parts.length - 1];
  }

  isImageFile(fileName: string): boolean {
    let result = false;
    // let ext: string = this.getExtension(fileName);
    result = /\.(png|jpeg|jpg|bmp|webp)$/i.test(fileName);
    return result;
  }

  loadFile(inputFile: any) {
    const self = this;
    const file: any = inputFile;
    const extFile: any = self.getExtension(file.name);
    self.uploadImage(file, extFile);
    const reader = new FileReader();
    reader.onload = function (event: any) {
      const dataUri = event.target.result;
      self.imageUrl = dataUri;
    };
    reader.onerror = function (event: any) {
      console.error('File could not be read! Code ' + event.target.error.code);
    };
    reader.readAsDataURL(inputFile);
  }

  uploadImage(file: File, extFile: string): void {
    const updateImageSub = this._api.shopboxApiService.accountService.addImage(file, extFile);
    this._api.apiType = API_TYPES.UPDATE_CLIENT_IMAGE;
    this._api.errorType = ERROR_TYPES.NORMAL;
    this._api.apiSubject.next(updateImageSub);
  }

  setUpdateImage(res: any) {
    if (res.type === HttpEventType.Response) {
      if (res.body) {
        res = res.body;
        this.imageUrl = res.image_medium;
        this.editClient(res.uid);
        this._changeDetectRef.markForCheck();
      }
    }
  }

  editClient(idImage: number) {
    const model = {};
    model['logo'] = idImage;
    const editClientSub = this._api.shopboxApiService.clientService.putClient(
      model,
      this.selectedCompany()!.uid,
    );
    this._api.apiType = API_TYPES.UPDATE_CLIENT_MAIN;
    this._api.errorType = ERROR_TYPES.NORMAL;
    this._api.apiSubject.next(editClientSub);
  }

  editClientComplete() {
    const company = this.selectedCompany();
    if (company) {
      company.icon = this.imageUrl;
      this.selectedCompany.set(company);
    }
    this.updateCurrentStoreCache();
  }

  changeProgress(val: boolean) {
    this.showProgress.set(val);
    this._sharedChanges.sharedProgressChanges(val);

    // Could be deleted later, since we are using signals
    // this._changeDetectRef.markForCheck();
  }

  ///////////////////////////////////
  // Refresh Section
  ///////////////////////////////////

  getTopMoveNum(event: any) {
    this.topMove = event;
  }

  getRotateArrow(event: any) {
    this.rotateArrow = event;
  }

  getStartTouch(event: any) {
    this.startTouch = event;
    this._changeDetectRef.markForCheck();
  }

  getEndTouch(event: any) {
    this.endTouch = event;
    this._changeDetectRef.markForCheck();
  }

  onPull(event: any) {
    if (event && !this.showProgress()) {
      this.refreshTab();
    }
  }

  getStaffProfile(): boolean {
    return this._storage.getStaffProfile();
  }

  getBreadCrumbsNav(): boolean {
    return this._storage.getBreadCrumbsNav();
  }

  getTrialToolbarStatus() {
    return this._storage.getTrialToolbarStatus();
  }

  setTrialToolbar(val: boolean) {
    this._storage.setTrialToolbarStatus(val);
  }

  /** Two-way binding target for p-drawer [(visible)]. Reads/writes drawer visibility via StorageService. */
  get navVisible(): boolean {
    return this.getDrawerVisibility();
  }

  set navVisible(val: boolean) {
    this.setDrawerVisibility(val);
    this._changeDetectRef.markForCheck();
  }

  toggleSideNav(): void {
    if (this.isSmallScreen()) {
      this.navVisible = !this.navVisible;
      if (this.navVisible) {
        this.setDrawerStatus(true);
      }
    } else {
      this.navVisible = !this.navVisible;
    }
  }

  /** Fired by CDK after clipboard copy attempt (`cdkCopyToClipboard`). */
  onClientIdCopied(copied: boolean): void {
    if (!copied) {
      return;
    }
    this._dialog.toast(this.translate.instant('CLIENT_ID_COPIED'), this._viewRef, 2000, false);
    this._changeDetectRef.markForCheck();
  }

  private updateTranslations(): void {
    this.translatedRefreshLabel.set(this.translate.instant('REFRESH_STORES'));
    this.translatedSearch.set(this.translate.instant('SEARCH'));
    this.translatedStoresList.set(this.translate.instant('STORES'));
    this.translatedClientId.set(this.translate.instant('CLIENT_ID'));
    this.translatedClickToCopyClientId.set(this.translate.instant('CLICK_TO_COPY_CLIENT_ID'));
  }

  ngOnDestroy() {
    this._overlayRef?.dispose();
    this._intercom.shutdown();
    this._api.destroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
